import { ChevronDown, ChevronUp, Copy, Trash2 } from 'lucide-react';
import React from 'react';
import { useWinScales } from '../hooks/useWinScales';

const WinScalesEditor = ({ betBody, setBetBody }) => {
  const {
    updateWinScale,
    removeWinScale,
    addWinScale,
    duplicateWinScale,
    moveWinScaleUp,
    moveWinScaleDown,
    clearAllWinScales,
    setPresetWinScales,
    generateLogicalSequence,
    validateWinScales,
  } = useWinScales(betBody, setBetBody);

  const validationErrors = validateWinScales();
  const hasErrors = validationErrors.length > 0;

  return (
    <div className="p-4 rounded-lg bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">Win Scales</h3>
        <div className="flex gap-2">
          <select
            onChange={(e) => {
              if (e.target.value) {
                setPresetWinScales(e.target.value);
                e.target.value = '';
              }
            }}
            defaultValue=""
            className="px-2 py-1 text-xs border border-gray-300 rounded"
          >
            <option value="">Пресети</option>
            <option value="basic">Базовий</option>
            <option value="detailed">Детальний</option>
            <option value="simple">Простий</option>
            <option value="advanced">Розширений</option>
          </select>
          <button
            onClick={clearAllWinScales}
            className="px-2 py-1 text-xs text-red-600 bg-red-100 rounded hover:bg-red-200"
            disabled={betBody.winsScale.length === 0}
          >
            Очистити
          </button>
        </div>
      </div>

      {/* Генератор логической последовательности */}
      <div className="p-3 mb-4 border border-blue-200 rounded bg-blue-50">
        <h4 className="mb-2 text-sm font-medium text-blue-800">Генератор послідовності</h4>
        <div className="grid grid-cols-4 gap-2 text-sm">
          <input type="number" placeholder="Мін (0)" className="px-2 py-1 border rounded" id="seq-min" />
          <input type="number" placeholder="Макс (1000)" className="px-2 py-1 border rounded" id="seq-max" />
          <input type="number" placeholder="Кроків (5)" className="px-2 py-1 border rounded" id="seq-steps" min="2" />
          <button
            onClick={() => {
              const min = parseInt(document.getElementById('seq-min').value) || 0;
              const max = parseInt(document.getElementById('seq-max').value) || 1000;
              const steps = parseInt(document.getElementById('seq-steps').value) || 5;
              generateLogicalSequence(min, max, steps);
            }}
            className="px-3 py-1 text-xs text-white bg-blue-500 rounded hover:bg-blue-600"
          >
            Генерувати
          </button>
        </div>
      </div>

      {/* Ошибки валидации */}
      {hasErrors && (
        <div className="p-2 mb-3 text-sm text-red-600 rounded bg-red-50">
          <div className="font-medium">Помилки валідації:</div>
          <ul className="mt-1 ml-4 list-disc">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Список Win Scales */}
      <div className="space-y-2">
        {betBody.winsScale.map((scale, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="w-6 text-xs text-gray-500">{index + 1}</span>
            <input
              type="text"
              value={scale}
              onChange={(e) => updateWinScale(index, e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Наприклад: 100-500, >1000, <100"
            />
            <div className="flex gap-1">
              <button
                onClick={() => moveWinScaleUp(index)}
                disabled={index === 0}
                className="p-1 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Вгору"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => moveWinScaleDown(index)}
                disabled={index === betBody.winsScale.length - 1}
                className="p-1 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Вниз"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
              <button
                onClick={() => duplicateWinScale(index)}
                className="p-1 text-blue-600 bg-blue-100 rounded hover:bg-blue-200"
                title="Дублювати"
              >
                <Copy className="w-3 h-3" />
              </button>
              <button
                onClick={() => removeWinScale(index)}
                className="p-1 text-red-600 bg-red-100 rounded hover:bg-red-200"
                title="Видалити"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}

        {betBody.winsScale.length === 0 && (
          <div className="p-4 text-sm text-center text-gray-500 border-2 border-gray-300 border-dashed rounded-md">
            Немає Win Scales. Додайте новий або використайте пресет.
          </div>
        )}

        <button
          onClick={addWinScale}
          className="w-full px-3 py-2 text-sm text-gray-600 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 hover:text-blue-600"
          type="button"
        >
          + Додати Win Scale
        </button>
      </div>

      {/* Статистика */}
      <div className="pt-3 mt-4 text-sm text-gray-600 border-t border-gray-200">
        Всього Win Scales: {betBody.winsScale.length}
        {hasErrors && <span className="ml-2 text-red-600">| {validationErrors.length} помилок</span>}
      </div>
    </div>
  );
};

export default WinScalesEditor;
